import React from 'react';
import { Modal, ModalProps, Box } from '@mantine/core';
import { WizardSidebar } from './WizardSidebar';
import type { WizardData } from './types';
import { useWizard } from './useWizard';
import { WizardStepper } from './WizardStepper';
import { useIsMdBreakpoint } from '../../hooks';

/**
 * WizardModal component
 */
export type WizardModalProps = ModalProps & WizardData;

/**
 * WizardModal component
 * @param steps - array of steps
 * @param title - modal title
 * @param onClose - function to close modal
 * @param modalProps - rest of modal props
 *
 * @example
 * ```tsx
 * <WizardModal
 *    title="Create new form"
 *    steps={[
 *      {
 *        label: 'Select template',
 *        content: StepSelectTemplate,
 *        icon: TemplatesIcon,
 *      },
 *      {
 *        label: 'Configure form',
 *        content: StepSettingsForm,
 *        icon: SettingsIcon,
 *      },
 *    ]}
 *    onClose={handleClose}
 *  />
 * ```
 *
 *  @see WizardStepRoot
 *  @see WizardStepContent
 *  @see WizardStepActions
 */
export const WizardModal: React.FC<WizardModalProps> = ({
  steps = [],
  title,
  onClose,
  ...modalProps
}) => {
  const isMdBreakpoint = useIsMdBreakpoint();
  const {
    currentStepLabel,
    nextStepLabel,
    currentStepIndex,
    CurrentStepComponent,
    currentState,
    doneStepsIndex,
    availableStepsIndex,
    totalSteps,
    close,
  } = useWizard({
    onClose,
    steps,
  });

  return (
    <Modal
      centered
      size={800}
      padding={0}
      withCloseButton={false}
      onClose={close}
      overflow="inside"
      {...modalProps}
    >
      <Box
        mah={isMdBreakpoint ? 638 : '80vh'}
        h={isMdBreakpoint ? 638 : undefined}
        sx={{
          display: 'flex',
          flexDirection: isMdBreakpoint ? 'row' : 'column',
        }}
      >
        {isMdBreakpoint ? (
          <WizardSidebar
            sx={{ flex: '1 0 290px', maxWidth: 290 }}
            title={title}
            steps={steps}
            doneStepsIndex={doneStepsIndex}
            currentStepIndex={currentStepIndex}
            availableStepsIndex={availableStepsIndex}
            goToStep={currentState.goToStep}
          />
        ) : (
          <WizardStepper
            title={title}
            currentStepIndex={currentStepIndex}
            stepsCount={totalSteps}
            currentStepTitle={currentStepLabel}
            nextStepTitle={nextStepLabel}
            onClose={close}
          />
        )}
        <CurrentStepComponent {...currentState} />
      </Box>
    </Modal>
  );
};

export default WizardModal;
